import React, { useEffect, useState } from "react";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";
import { DialogBox, DialogBoxFooterType, Loader, LoaderTypes, TextArea, TextInput } from "~/core";
import { actions as notificationActions } from "~/notifications";
import "../rec-module.css";
import { messages } from "../i18n-messages";
import * as selectors from "../../selectors";
import { IFormatter } from "@ai360/core";

interface ISendRecValidationProps {
    isOpen: boolean;
    onSend: (evt: { workOrder: string; instructions: string }) => void;
    onClose: () => void;
    onApiError: (err, message) => void;
    intl: IFormatter;
    sendDestination: string;
    isLoading: boolean;
    isBatch: boolean;
    userGuid: string;
    rollingMessage: IRollingMessageObject;
}

interface IRollingMessageObject {
    id: number;
    step: number;
    message: string;
}

const SendRecValidation = (props: ISendRecValidationProps): JSX.Element => {
    const { isOpen, isLoading, isBatch, onSend, onClose, sendDestination, rollingMessage } = props;
    const { formatMessage } = props.intl;

    const [workOrder, setWorOrder] = useState<string>("");
    const [instructions, setInstructions] = useState<string>("");
    const [disableSend, setDisableSend] = useState<boolean>(false);
    const [validationFeedback, setValidationFeedback] = useState<string>("");
    const [rollingMessageHolder, setRollingMessageHolder] = useState<IRollingMessageObject[]>([]);
    const [showLoader, setShowLoader] = useState<boolean>(false);

    useEffect(() => {
        if (rollingMessage && showLoader) {
            setShowLoader(false);
        }
        if (rollingMessage?.id == null) {
            const oldValidationFeedback = validationFeedback;
            const newValidationFeedback =
                oldValidationFeedback.length === 0
                    ? rollingMessage?.message
                    : oldValidationFeedback + "\n" + rollingMessage?.message;
            setValidationFeedback(newValidationFeedback);
        }
        if (rollingMessage?.id > 0) {
            setRollingMessageHolder([rollingMessage, ...rollingMessageHolder]);
            const rollingMessageObjects = [rollingMessage, ...rollingMessageHolder];
            const sortedRollingMessages = [...rollingMessageObjects]
                .sort((a, b) => a.step - b.step)
                .sort((c, d) => c.id - d.id)
                .map((x) => x.message);
            const newValidationFeedback = sortedRollingMessages.join("\n");
            setValidationFeedback(newValidationFeedback);
        }
    }, [rollingMessage]);

    useEffect(() => {
        setValidationFeedback("");
        setRollingMessageHolder([]);
    }, []);

    const onWorkPlanSend = () => {
        setShowLoader(true);
        onSend({
            workOrder,
            instructions,
        });
        setDisableSend(true);
    };

    if (!isOpen) {
        return null;
    }

    return (
        <DialogBox
            action="Send"
            actionDisabled={disableSend}
            className={isBatch ? "validation-feedback-modal-batch" : "validation-feedback-modal"}
            draggable
            footerType={DialogBoxFooterType.ACTION_CLOSE}
            forceOverflow
            isOpen={isOpen}
            onAction={onWorkPlanSend}
            onClose={onClose}
            title={`${formatMessage(
                isBatch ? messages.sendRecsToText : messages.sendRecToText
            )} ${sendDestination}`}
            unrestricted
        >
            {showLoader && <Loader />}
            <TextInput
                placeholderText={formatMessage(messages.workOrderText)}
                value={workOrder}
                onChange={(value) => setWorOrder(value)}
            ></TextInput>
            <TextArea
                containerClassNames={["work-instructions"]}
                placeholderText={formatMessage(messages.instructionsText)}
                value={instructions}
                onChange={(value) => setInstructions(value)}
            ></TextArea>
            <TextArea
                placeholderText={formatMessage(messages.statusMessagesText)}
                value={validationFeedback}
                readOnly
                disabled
            ></TextArea>
            {!isLoading ? null : <Loader type={LoaderTypes.LINE_SCALE_PULSE_OUT} />}
        </DialogBox>
    );
};

const mapStateToProps = (state) => {
    const { isLoading } = selectors.getModuleState(state);
    const rollingMessage = state["NOTIFICATION_STATE"].messageForValidationFeedback;
    return {
        isLoading,
        rollingMessage,
    };
};

const mapDispatchToProps = (dispatch) => ({
    onApiError: (err, message) => dispatch(notificationActions.apiCallError(err, null, message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(SendRecValidation));
